import { AfterViewInit, Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { User } from 'app/classes/user';
import { CopyPlanDialogComponent, CopyPlanDialogComponentData } from 'app/core/components/copy-plan-dialog/copy-plan-dialog.component';
import { SelectGroupsDialogComponent } from 'app/core/components/select-groups-dialog/select-groups-dialog.component';
import { UserSearchComponent } from 'app/core/components/user-search/user-search.component';
import { DocumentService } from 'app/document/services/document.service';
import * as GuidedTourActions from 'app/guided-tour/state/guided-tour.actions';
import { GuidedTourSelectors } from 'app/guided-tour/state/guided-tour.selectors';
import { AuthService } from 'app/services/auth.service';
import { GuidedTourDataService } from 'app/services/guided-tour-data.service';
import { OfflineService } from 'app/services/offline.service';
import { PlanMetadata } from 'fitforce-document-sync';
import { Observable, of, Subscription } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { InitializeLoadScreenComponent } from '../initialize-load-screen/initialize-load-screen.component';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'planning',
  templateUrl: './planning.component.html',
  styleUrls: ['./planning.component.scss']
})
export class PlanningComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  sortOptions = [
    {
      name: 'Plan date',
      code: ['isAvailableOffline', 'month.startDate'],
      docCode: ['isDownloaded', 'startDate'],
    },
    {
      name: 'Plan name',
      code: ['isAvailableOffline', 'sortName'],
      docCode: ['isDownloaded', 'name'],
    },
  ];
  displayOrder = this.sortOptions[0];
  searchText: string;
  isOnline: boolean;
  currentUser: User;
  planningDataServiceInitSubscription: Subscription = new Subscription();
  documentServiceInitSubscription: Subscription = new Subscription();
  currentUserSubscription: Subscription = new Subscription();

  guidedTourToggledOn = false;
  guidedTourStatusSub: Subscription;

  @Select(GuidedTourSelectors.toggleStatus)guidedTourToggleStatus$: Observable<boolean>;

  constructor(
    public documentService: DocumentService,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private offline: OfflineService,
    private guidedTourDataService: GuidedTourDataService,
    private store: Store
  ) {}

  ngOnInit() {
    this.offline.tryPingServer();
    this.isOnline = this.offline.isOnline();
    this.offline.status.subscribe(value => {
      this.isOnline = value;
    });

    // every time we load the planning page, attempt to refresh plans from offline database
    // this.planningDataServiceInitSubscription = this.service.init().subscribe(() => this.service.getPlans());
    this.documentServiceInitSubscription = this.documentService.init().subscribe();

    this.currentUserSubscription = this.authService.currentUserObs.subscribe(user => this.currentUser = user);

    this.store.dispatch(new GuidedTourActions.GetGuidedTourToggleStatus());
  }

  ngAfterViewInit(): void {
    this.guidedTourStatusSub = this.guidedTourToggleStatus$.subscribe(status => {
      if (status !== undefined) {
        this.guidedTourToggledOn = status;
        if (this.guidedTourToggledOn) {
          setTimeout(() => this.guidedTourDataService.startGuidedTour('plan-library'), 1000);
        }
      }
    });
  }
  ngOnChanges(): void {}

  ngOnDestroy(): void {
    this.planningDataServiceInitSubscription.unsubscribe();
    this.documentServiceInitSubscription.unsubscribe();
    if (this.guidedTourStatusSub) {
      this.guidedTourStatusSub.unsubscribe();
    }
  }

  sync() {
    this.dialog.open(InitializeLoadScreenComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true,
      closeOnNavigation: false,
      data: {
        sync: true,
        autoClose: true,
      },
    });
  }

  createPlan() {
    this.router.navigate(['document/create-plan']);
  }

  editPlan(plan: PlanMetadata) {
    this.router.navigate([`document/create-plan/${plan.uuid}`]);
  }

  viewPlan(id) {
    this.router.navigate(['view-plan', id]);
  }

  goTo(plan: PlanMetadata) {
    if (plan instanceof PlanMetadata) {
      if (this.isOnline) {
        this.dialog.open(InitializeLoadScreenComponent, {
          width: '500px',
          maxWidth: '90vw',
          closeOnNavigation: false,
          disableClose: true,
          data: {
            autoClose: true,
            obs: this.documentService.syncPlan(plan.uuid)
          },
        }).beforeClosed().subscribe(() => {
          this.router.navigate([`document/planning/${plan.uuid}`]);
        });
      } else if (plan.isDownloaded) {
        this.router.navigate([`document/planning/${plan.uuid}`]);
      }
    }
  }

  sharePlan(plan: PlanMetadata) {
    if (plan instanceof PlanMetadata) {
      this.dialog.open(UserSearchComponent, <MatDialogConfig> {
        width: '700px',
        data: {
          selectedUsers: plan.sharedWithUsers && plan.sharedWithUsers.length > 0
            ? plan.sharedWithUsers.map(user => (new User(user)))
            : [],
          disableUnshare: true,
          submitButtonText: 'Share',
          disableSelf: true,
          currentUserUUID: this.currentUser.uuid
        }
      }).afterClosed().subscribe(data => {
        if (data) {
          this.documentService.sharePlan(plan.uuid, data.users.map(user => user.uuid)).subscribe();
        }
      });
    }
  }

  publishPlan(plan: PlanMetadata) {
     if (plan instanceof PlanMetadata) {
      this.dialog.open(SelectGroupsDialogComponent, {
        width: '400px',
        data: {
          selected: plan.publishedToGroups.map(group => group.uuid),
        }
      }).beforeClosed().pipe(
        concatMap((result: string[]) => {
          if (result) {
            return this.documentService.publishPlan(plan.uuid, result);
          } else {
            return of(null);
          }
        })
      ).subscribe();
    }
  }

  deletePlan(plan: PlanMetadata) {
    this.dialog.open(ConfirmationDialogComponent, {
      data: 'Are you sure you want to delete this plan?'
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        if (plan instanceof PlanMetadata) {
          this.documentService.deletePlan(plan.uuid).subscribe();
        }
      }
    });
  }

  isOwner(createdByUserUUID: any): boolean {
    return this.currentUser && createdByUserUUID === this.currentUser.uuid;
  }

  copyPlan(plan: PlanMetadata) {
    const data: CopyPlanDialogComponentData = {
      plan: plan,
      userUUID: this.currentUser.uuid
    };
    this.dialog.open(CopyPlanDialogComponent, {
      width: '450px',
      data: data
    });
  }
}
